import { useContext, useMemo } from 'react';

import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { useNavigation } from '@atom/hooks/useNavigation';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Tooltip } from '@atom/mui';
import { WorkOrderType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import {
  getLocalDisplayDate,
  setDisplayDate,
  setDurationDisplay,
} from '@atom/utilities/timeUtilities';

import classes from './workOrderPreviewHeader.module.css';

const PreviewHeaderInfo = () => {
  const [, { navigate }] = useNavigation();
  const { workOrderDetail } = useContext(WorkOrderPreviewContext);
  const { schemas = [] } = useSchemas();

  const inventorySchemaName = useMemo(() => {
    const foundSchema = schemas.find(
      schema => schema.id === workOrderDetail?.inventoryAssetSchemaId,
    );

    return foundSchema?.name || '-';
  }, [workOrderDetail?.inventoryAssetSchemaId, schemas]);

  const navigateToInventoryDetail = () =>
    navigate(`/inventory/${workOrderDetail.inventoryAssetId}`);

  const inventoryNameOnClick = workOrderDetail?.inventoryAssetId
    ? navigateToInventoryDetail
    : () => {};

  const showActualCost = hasRolePermissions(ROLE_SETS.MANAGER);
  const showInventoryInfo =
    workOrderDetail?.inventoryAssetSchemaId &&
    workOrderDetail?.type === WorkOrderType.DEFAULT;

  return (
    <div className={classes.headerInfoSection}>
      {showInventoryInfo && (
        <div className={classes.infoSectionBlock}>
          <div className={classes.infoContent}>
            <span>Asset Name: </span>
            <Tooltip title={workOrderDetail?.inventoryAssetName}>
              <span
                className={classes.inventoryName}
                onClick={inventoryNameOnClick}
              >
                {workOrderDetail?.inventoryAssetName || '-'}
              </span>
            </Tooltip>
          </div>
          <div className={classes.infoContent}>
            <span>Asset Type: </span>
            <Tooltip title={inventorySchemaName}>
              <span className={classes.inventoryName}>
                {inventorySchemaName}
              </span>
            </Tooltip>
          </div>
        </div>
      )}
      {workOrderDetail?.appointmentsEnabled ? (
        <>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoContent}>{`Planned Start Date: ${
              getLocalDisplayDate(
                workOrderDetail.plannedStartDate,
                'MM/dd/yyyy hh:mm a',
                false,
              ) || '-'
            }`}</div>
            <div className={classes.infoContent}>{`Due Date: ${
              getLocalDisplayDate(
                workOrderDetail.dueDate,
                'MM/dd/yyyy hh:mm a',
                false,
              ) || '-'
            }`}</div>
          </div>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoContent}>{`Start Date: ${setDisplayDate(
              workOrderDetail?.startTime,
            )}`}</div>
            <div
              className={classes.infoContent}
            >{`Completed Date: ${setDisplayDate(
              workOrderDetail?.completionDate,
            )}`}</div>
          </div>
        </>
      ) : (
        <>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoContent}>{`Start Date: ${setDisplayDate(
              workOrderDetail?.startTime,
            )}`}</div>
            <div
              className={classes.infoContent}
            >{`Completed Date: ${setDisplayDate(
              workOrderDetail?.completionDate,
            )}`}</div>
          </div>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoContent}>{`Due Date: ${setDisplayDate(
              workOrderDetail?.dueDate,
            )}`}</div>
            <div
              className={classes.infoContent}
            >{`Duration: ${setDurationDisplay(
              workOrderDetail?.duration,
            )}`}</div>
          </div>
        </>
      )}
      <div className={classes.infoSectionBlock}>
        <div
          className={classes.infoContent}
        >{`Estimated Cost: ${numberToLocaleString(
          workOrderDetail.estimatedCost,
        )}`}</div>
        {showActualCost && (
          <div
            className={classes.infoContent}
          >{`Actual Cost: ${numberToLocaleString(
            workOrderDetail.actualCost,
          )}`}</div>
        )}
      </div>
    </div>
  );
};

export default PreviewHeaderInfo;
